import React, { useState, useEffect } from 'react';
import { Modal, Descriptions, Tag, Spin, Table, Typography, List, Button } from 'antd';
import { DownloadOutlined, FileOutlined } from '@ant-design/icons';
import axios from '../../services/axiosConfig';
import { INVOICE_API } from '../../constants/api';

const { Text, Title } = Typography;

const InvoiceDetailModal = ({ visible, record, onCancel }) => {
  const [loading, setLoading] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const fetchInvoiceDetail = async (invoiceId) => {
    try {
      setLoading(true);
      const response = await axios.get(INVOICE_API.DETAIL(invoiceId));
      setDetailData(response.data.data);
    } catch (error) {
      console.error('Lỗi khi tải chi tiết hóa đơn:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentHistory = async (invoiceId) => {
    try {
      setHistoryLoading(true);
      const response = await axios.get(INVOICE_API.PAYMENTS(invoiceId));
      setPaymentHistory(response.data.data || []);
    } catch (error) {
      console.error('Lỗi khi tải lịch sử thanh toán:', error);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (visible && record?.id) {
      fetchInvoiceDetail(record.id);
      fetchPaymentHistory(record.id);
    }
  }, [visible, record]);

  if (!record) return null;

  const getStatusTag = (status) => {
    const statusConfig = {
      pending: { color: 'gold', text: 'Chờ thanh toán' },
      paid: { color: 'green', text: 'Đã thanh toán' },
      unpaid: { color: 'gold', text: 'Chưa thanh toán' },
      partial: { color: 'orange', text: 'Chưa thanh toán đủ' },
      overdue: { color: 'red', text: 'Quá hạn' },
    };
    return <Tag color={statusConfig[status]?.color}>{statusConfig[status]?.text}</Tag>;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('vi-VN', { 
      style: 'currency', 
      currency: 'VND' 
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '--';
    return new Date(dateString).toLocaleDateString('vi-VN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const paymentHistoryColumns = [
    {
      title: 'Ngày thanh toán',
      dataIndex: 'PaymentDate',
      key: 'PaymentDate',
      render: (date) => formatDate(date),
    },
    {
      title: 'Số tiền',
      dataIndex: 'Amount',
      key: 'Amount',
      render: (amount) => formatCurrency(amount),
    },
    {
      title: 'Phương thức',
      dataIndex: 'PaymentMethod',
      key: 'PaymentMethod',
      render: (method) => method || '--',
    },
    {
      title: 'Ghi chú',
      dataIndex: 'Note',
      key: 'Note',
      render: (note) => note || '--',
    }
  ];

  return (
    <Modal
      title={<Title level={4} style={{ margin: 0 }}>Chi tiết hóa đơn tổng hợp</Title>}
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={900}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <Descriptions bordered column={2} size="small">
            <Descriptions.Item label="Mã hóa đơn" span={1}>
              <Text strong>{detailData?.InvoiceID || record.invoiceNumber}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Trạng thái" span={1}>
              {getStatusTag(detailData?.Status || record.status)}
            </Descriptions.Item>

            <Descriptions.Item label="Mã hợp đồng">
              {detailData?.ContractID || '--'}
            </Descriptions.Item>
            <Descriptions.Item label="Kỳ thanh toán">
              {detailData?.Period || record.period}
            </Descriptions.Item>

            <Descriptions.Item label="Khách thuê" span={1}>
              {detailData?.TenantName || record.tenantName}
            </Descriptions.Item>
            <Descriptions.Item label="Điện thoại" span={1}>
              {detailData?.TenantPhone || '--'}
            </Descriptions.Item>

            <Descriptions.Item label="Xưởng" span={1}>
              {detailData?.FactoryName || record.factoryName}
            </Descriptions.Item>
            <Descriptions.Item label="Hạn thanh toán" span={1}>
              <Text type="danger">{detailData?.DueDate ? formatDate(detailData.DueDate) : record.dueDate}</Text>
            </Descriptions.Item>

            {detailData?.Note && (
              <Descriptions.Item label="Ghi chú" span={2}>
                {detailData.Note}
              </Descriptions.Item>
            )}
          </Descriptions>

          {detailData && (
            <>
              <div>
                <Title level={5}>Chi tiết các khoản thu</Title>
                <Descriptions bordered column={1} size="small">
                  <Descriptions.Item label="1. Tiền thuê xưởng">
                    <Text strong>{formatCurrency(detailData.RentAmount)}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="2. Phí dịch vụ & hạ tầng">
                    <Text strong>{formatCurrency(detailData.ServiceAmount)}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="3. Tiền điện">
                    <Text strong>{formatCurrency(detailData.ElectricityAmount)}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="4. Tiền nước sạch">
                    <Text strong>{formatCurrency(detailData.WaterAmount)}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="5. Tiền nước thải">
                    <Text strong>{formatCurrency(detailData.OtherAmount)}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="TỔNG CỘNG" style={{ backgroundColor: '#fafafa' }}>
                    <Text strong style={{ fontSize: '16px', color: '#1890ff' }}>
                      {formatCurrency(detailData.Amount)}
                    </Text>
                  </Descriptions.Item>
                </Descriptions>
              </div>

              <div>
                <Title level={5}>Tình trạng thanh toán</Title>
                <Descriptions bordered column={2} size="small">
                  <Descriptions.Item label="Đã thanh toán">
                    <Text type="success" strong>{formatCurrency(detailData.PaidAmount)}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="Còn thiếu">
                    <Text type="danger" strong>{formatCurrency(detailData.Amount - detailData.PaidAmount)}</Text>
                  </Descriptions.Item>
                </Descriptions>
              </div>

              <div>
                <Title level={5}>File đính kèm</Title>
                {(() => {
                  let files = [];
                  if (detailData?.Files) {
                    try {
                      files = typeof detailData.Files === 'string' ? JSON.parse(detailData.Files) : detailData.Files;
                    } catch (e) {
                      console.error('Lỗi khi parse danh sách file:', e);
                    }
                  }
                  
                  if (files.length === 0) {
                    return <Text type="secondary">Không có file đính kèm</Text>;
                  }
                  
                  return (
                    <List
                      size="small"
                      bordered
                      dataSource={files}
                      renderItem={item => (
                        <List.Item
                          actions={[
                            <a 
                              key="download" 
                              href={`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/uploads/invoices/${item}`} 
                              target="_blank" 
                              rel="noreferrer"
                            >
                              <Button type="link" size="small" icon={<DownloadOutlined />}>Tải / Xem</Button>
                            </a>
                          ]}
                        >
                          <List.Item.Meta
                            avatar={<FileOutlined style={{ fontSize: '20px', color: '#1890ff' }} />}
                            title={item}
                          />
                        </List.Item>
                      )}
                    />
                  );
                })()}
              </div>

              <div>
                <Title level={5}>Lịch sử thanh toán</Title>
                {historyLoading ? (
                  <Spin size="small" />
                ) : (
                  <Table 
                    dataSource={paymentHistory} 
                    columns={paymentHistoryColumns} 
                    rowKey={(record) => record.PaymentID || record.id || Math.random()}
                    pagination={false}
                    size="small"
                    bordered
                    locale={{ emptyText: 'Chưa có lịch sử thanh toán' }}
                  />
                )}
              </div>
            </>
          )}
        </div>
      )}
    </Modal>
  );
};

export default InvoiceDetailModal;